/* ============================================================
   storage — the media player.

   The browser's own <video controls> is a play button, a bar and a
   volume slider. Watching a folder of clips wants more than that:
   frame stepping, speed, A→B repeat, snapshots, next/previous clip,
   and a position that survives closing the tab. So this is a player
   in the VLC sense — every control drawn here, driven by one
   <video> (or <audio>) element underneath.

   Everything is keyboard-reachable and every key is listed in the
   help overlay (?). Volume, mute, speed and per-file position are
   remembered in localStorage. Markers are the one thing that lives
   on the server, because they are shared: see Markers.js.

   The element is fed the same inline URL the rest of the preview
   pane uses, so bytes still come straight from nginx.
   ============================================================ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MarkerPanel, MarkerPins, useMarkers } from './Markers';
import { TrimNotice, TrimTools } from './Trim';
import { api } from './api';
import { reachableNow } from './direct';

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3, 4];
const FITS = [
  { id: 'contain', label: 'Fit' },
  { id: 'cover', label: 'Fill' },
  { id: 'fill', label: 'Stretch' },
];
const PREFS_KEY = 'st.player.prefs';
const QUALITY_KEY = 'st.player.quality';

/* Auto: the best copy the connection can carry. Chrome reports downlink in
   Mb/s, capped at 10 — so 10 means "at least 10", which carries the 1080p
   copy. With no estimate, the 1080p copy: it is ~8 Mb/s, not the original's
   130. The original is never an automatic choice while a copy exists. */
function autoQuality(list) {
  const copies = list.filter((q) => q.id !== 'original');
  if (!copies.length) return list[0]?.id || null;
  const down = navigator.connection?.downlink;
  const byRate = [...copies].sort((a, b) => b.bitrate - a.bitrate);
  if (!down) return (copies.find((q) => q.id === '1080') || byRate[0]).id;
  const fits = byRate.find((q) => q.bitrate * 1.4 <= down * 1e6 || (down >= 10 && q.bitrate <= 12e6));
  return (fits || byRate[byRate.length - 1]).id;
}
const POS_KEY = 'st.player.pos';
// Below this, "where you left off" is just the beginning.
const RESUME_MIN = 25;
// A frame step assumes 30 fps unless the browser tells us otherwise.
const FALLBACK_FPS = 30;

/* How far the browser has buffered past where it is now. */
function bufferedAhead(el) {
  try {
    const b = el.buffered;
    for (let i = 0; i < b.length; i += 1) {
      if (b.start(i) <= el.currentTime && b.end(i) >= el.currentTime) return b.end(i);
    }
    return b.length ? b.end(b.length - 1) : 0;
  } catch { return 0; }   // buffered throws before metadata on some browsers
}

const pad = (n) => String(Math.floor(n)).padStart(2, '0');
export function fmtTime(s) {
  if (!Number.isFinite(s) || s < 0) return '--:--';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

function readJSON(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch { return fallback; }
}
function writeJSON(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}

/* Saved playback positions, keyed by file id and pruned so the store
   cannot grow without end. */
function loadPositions() { return readJSON(POS_KEY, {}); }

/** Where this file was left off, for anything outside the player that wants
 *  to carry the position — a link to the viewer, for one. */
export function savedPosition(id) {
  const at = loadPositions()[id];
  return Number.isFinite(at) ? at : 0;
}
function savePosition(id, seconds, duration) {
  if (!id) return;
  const all = loadPositions();
  const done = !Number.isFinite(duration) || seconds >= duration - 10;
  if (!Number.isFinite(seconds) || seconds < RESUME_MIN || done) delete all[id];
  else all[id] = Math.round(seconds);
  const keys = Object.keys(all);
  if (keys.length > 200) delete all[keys[0]];
  writeJSON(POS_KEY, all);
}

/* ─── icons ──────────────────────────────────────────────────────────────
   From ojee-ui's icons.json like everything else. They used to be drawn
   here on the grounds that nothing else uses them, which is true and was
   still how the player ended up with its own `close`, `play` and `pause`
   at a different weight from the three other sets in this codebase. */
function Glyph({ name, size = 16 }) {
  const shapes = {
    play: <path d="M320-203v-560l440 280-440 280Zm60-280Zm0 171 269-171-269-171v342Z" />,
    pause: <path d="M525-200v-560h235v560H525Zm-325 0v-560h235v560H200Zm385-60h115v-440H585v440Zm-325 0h115v-440H260v440Zm0-440v440-440Zm325 0v440-440Z" />,
    prev: <path d="M220-240v-480h60v480h-60Zm520 0L394-480l346-240v480Zm-60-240Zm0 125v-250L499-480l181 125Z" />,
    next: <path d="M680-240v-480h60v480h-60Zm-460 0v-480l346 240-346 240Zm60-240Zm0 125 181-125-181-125v250Z" />,
    back10: <path d="M339.5-108Q274-136 225-185t-77-114.5Q120-365 120-440h60q0 125 87.5 212.5T480-140q125 0 212.5-87.32T780-440q0-125.36-85-212.68Q610-740 485-740h-22l73 73-42 42-147-147 147-147 41 41-78 78h23q75 0 140.5 28T735-695q49 49 77 114.5T840-440q0 75-28 140.5T735-185q-49 49-114.5 77T480-80q-75 0-140.5-28ZM360-310v-212h-54v-49h104v261h-50Zm147 0q-18.7 0-31.35-12.65Q463-335.3 463-354v-173q0-18.7 12.65-31.35Q488.3-571 507-571h83q18.7 0 31.35 12.65Q634-545.7 634-527v173q0 18.7-12.65 31.35Q608.7-310 590-310h-83Zm6-50h71v-162h-71v162Z" />,
    fwd10: <path d="M360-310v-212h-54v-49h104v261h-50Zm147 0q-18.7 0-31.35-12.65Q463-335.3 463-354v-173q0-18.7 12.65-31.35Q488.3-571 507-571h83q18.7 0 31.35 12.65Q634-545.7 634-527v173q0 18.7-12.65 31.35Q608.7-310 590-310h-83Zm6-50h71v-162h-71v162ZM339.5-108Q274-136 225-185t-77-114.5Q120-365 120-440t28-140.5Q176-646 225-695t114.5-77Q405-800 480-800h21l-78-78 41-41 147 147-147 147-41-41 74-74h-17q-125.36 0-212.68 87.32Q180-565.36 180-440q0 125.36 87.5 212.68Q355-140 480-140t212.5-87.32Q780-314.64 780-440h60q0 75-28 140.5T735-185q-49 49-114.5 77T480-80q-75 0-140.5-28Z" />,
    frameBack: <path d="M240-240v-480h60v480h-60Zm447-3L453-477l234-234 43 43-191 191 191 191-43 43Z" />,
    frameFwd: <path d="m272-245-43-43 192-192-192-192 43-43 235 235-235 235Zm388 5v-480h60v480h-60Z" />,
    volume: <path d="M560-131v-62q97-28 158.5-107.5T780-481q0-101-61-181T560-769v-62q124 28 202 125.5T840-481q0 127-78 224.5T560-131ZM120-360v-240h160l200-200v640L280-360H120Zm420 48v-337q55 17 87.5 64T660-480q0 57-33 104t-87 64ZM420-648 307-540H180v120h127l113 109v-337Zm-94 168Z" />,
    volumeLow: <path d="M200-360v-240h160l200-200v640L360-360H200Zm420 48v-337q54 17 87 64t33 105q0 59-33 105t-87 63ZM500-648 387-540H260v120h127l113 109v-337ZM378-480Z" />,
    mute: <path d="M813-56 681-188q-28 20-60.5 34.5T553-131v-62q23-7 44.5-15.5T638-231L473-397v237L273-360H113v-240h156L49-820l43-43 764 763-43 44Zm-36-232-43-43q20-34 29.5-71.92Q773-440.85 773-481q0-103.32-60-184.66T553-769v-62q124 28 202 125.5T833-481q0 51-14 100t-42 93ZM643-422l-90-90v-130q47 22 73.5 66t26.5 96q0 15-2.5 29.5T643-422ZM473-592 369-696l104-104v208Zm-60 286v-150l-84-84H173v120h126l114 114Zm-42-192Z" />,
    full: <path d="M120-120v-193h60v133h133v60H120Zm527 0v-60h133v-133h60v193H647ZM120-647v-193h193v60H180v133h-60Zm660 0v-133H647v-60h193v193h-60Z" />,
    unfull: <path d="M253-120v-133H120v-60h193v193h-60Zm394 0v-193h193v60H707v133h-60ZM120-647v-60h133v-133h60v193H120Zm527 0v-193h60v133h133v60H647Z" />,
    pip: <path d="M140-160q-24 0-42-18t-18-42v-520q0-24 18-42t42-18h680q24 0 42 18t18 42v520q0 24-18 42t-42 18H140Zm0-60h680v-520H140v520Zm0 0v-520 520Zm306-45h335v-257H446v257Zm60-60v-137h215v137H506Z" />,
    speed: <path d="M473.5-303.5Q517-305 537-336l216-339-335 219q-30 20-32 64t21 67q23 23 66.5 21.5ZM478-799q57 0 119 18.5T716-717l-52 37q-45-30-96.5-44.5T477.98-739q-140.47 0-239.23 100.22Q140-538.57 140-396.02 140-351 152.5-305q12.5 46 35.5 85h579q22-36 35-84t13-94q0-42-12.5-90.5T758-578l39-52q38 56 57 112.5T875-404q2 60-12 113t-41 98q-12 23-25.5 28t-33.5 5H192q-17 0-33.5-8.5T134-193q-26-48-40-97.5T80-396q0-83 31.5-156.5t85.5-128Q251-735 323.68-767T478-799Zm-9 331Z" />,
    loop: <path d="M280-80 120-240l160-160 42 44-86 86h464v-160h60v220H236l86 86-42 44Zm-80-450v-220h524l-86-86 42-44 160 160-160 160-42-44 86-86H260v160h-60Z" />,
    ab: <path d="M170-700h230v60H230v420h170v60H170v-540ZM790-700H560v60h170v420H560v60h230v-540ZM490 -470L600 -580L600 -500L660 -500L660 -440L600 -440L600 -360L490 -470Z" />,
    snapshot: <path d="M479.5-267q72.5 0 121.5-49t49-121.5q0-72.5-49-121T479.5-607q-72.5 0-121 48.5t-48.5 121q0 72.5 48.5 121.5t121 49Zm0-60q-47.5 0-78.5-31.5t-31-79q0-47.5 31-78.5t78.5-31q47.5 0 79 31t31.5 78.5q0 47.5-31.5 79t-79 31.5ZM140-120q-24 0-42-18t-18-42v-513q0-23 18-41.5t42-18.5h147l73-87h240l73 87h147q23 0 41.5 18.5T880-693v513q0 24-18.5 42T820-120H140Zm0-60h680v-513H645l-73-87H388l-73 87H140v513Zm340-257Z" />,
    captions: <path d="M180-160q-24 0-42-18t-18-42v-520q0-24 18-42t42-18h600q24 0 42 18t18 42v520q0 24-18 42t-42 18H180Zm0-60h600v-520H180v520Zm90-141h142q12.75 0 21.38-8.63Q442-378.25 442-391v-42h-50v22H290v-138h102v22h50v-42q0-12.75-8.62-21.38Q424.75-599 412-599H270q-12.75 0-21.37 8.62Q240-581.75 240-569v178q0 12.75 8.63 21.37Q257.25-361 270-361Zm279 0h142q12 0 21-9t9-21v-42h-50v22H569v-138h102v22h50v-42q0-12-9-21t-21-9H549q-12 0-21 9t-9 21v178q0 12 9 21t21 9ZM180-220v-520 520Z" />,
    aspect: <path d="M577-274h191v-194h-60v134H577v60ZM193-492h60v-134h131v-60H193v194Zm-53 332q-24 0-42-18t-18-42v-520q0-24 18-42t42-18h680q24 0 42 18t18 42v520q0 24-18 42t-42 18H140Zm0-60h680v-520H140v520Zm0 0v-520 520Z" />,
    rotate: <path d="M527-80v-60q35-6 69-19.5t66-37.5l43 44q-40 30-85 48t-93 25Zm-90 0q-137-21-226-122t-89-241q0-75 28-140.5T227-698q49-49 114.5-77T482-803h20l-79-79 44-44 153 153-153 153-44-44 79-79h-20q-127 0-213.5 86.5T182-443q0 117 72.5 199.5T437-140v60Zm331-136-44-44q23-30 36.5-65t18.5-73h61q-7 51-25.5 97.5T768-216Zm72-272h-61q-5-35-18.5-70T724-626l44-43q29 40 47 85.5t25 95.5Z" />,
    stats: <path d="M284-277h60v-205h-60v205Zm332 0h60v-420h-60v420Zm-166 0h60v-118h-60v118Zm0-205h60v-60h-60v60ZM180-120q-24 0-42-18t-18-42v-600q0-24 18-42t42-18h600q24 0 42 18t18 42v600q0 24-18 42t-42 18H180Zm0-60h600v-600H180v600Zm0-600v600-600Z" />,
    help: <path d="M511-258q11-11 11-27t-11-27q-11-11-27-11t-27 11q-11 11-11 27t11 27q11 11 27 11t27-11Zm-62-135h59q0-26 6.5-47.5T555-490q31-26 44-51t13-55q0-53-34.5-85T486-713q-49 0-86.5 24.5T345-621l53 20q11-28 33-43.5t52-15.5q34 0 55 18.5t21 47.5q0 22-13 41.5T508-512q-30 26-44.5 51.5T449-393Zm31 313q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-156t86-127Q252-817 325-848.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 82-31.5 155T763-197.5q-54 54.5-127 86T480-80Zm0-60q142 0 241-99.5T820-480q0-142-99-241t-241-99q-141 0-240.5 99T140-480q0 141 99.5 240.5T480-140Zm0-340Z" />,
    audio: <path d="M286.5-163.5Q243-207 243-270t43.5-106.5Q330-420 393-420q28 0 50.5 8t39.5 22v-450h234v135H543v435q0 63-43.5 106.5T393-120q-63 0-106.5-43.5Z" />,
    newtab: <path d="M180-120q-24 0-42-18t-18-42v-600q0-24 18-42t42-18h279v60H180v600h600v-279h60v279q0 24-18 42t-42 18H180Zm202-219-42-43 398-398H519v-60h321v321h-60v-218L382-339Z" />,
    marker: <path d="M200-120v-665q0-24 18-42t42-18h440q24 0 42 18t18 42v665L480-240 200-120Zm60-91 220-93 220 93v-574H260v574Zm0-574h440-440Z" />,
    markerAdd: <path d="M200-120v-665q0-24 18-42t42-18h290v60H260v574l220-93 220 93v-334h60v425L480-240 200-120Zm60-665h290-290Zm440 180v-90h-90v-60h90v-90h60v90h90v60h-90v90h-60Z" />,
    cut: <path d="M782-114 481-415 364-298q11 17 13.5 33t2.5 35q0 64-43 107T230-80q-64 0-107-43T80-230q0-64 43-107t107-43q18 0 35.5 5t36.5 15l116-116-118-118q-17 8-34.5 11t-35.5 3q-64 0-107-43T80-730q0-64 43-107t107-43q64 0 107 43t43 107q0 19-2.5 36T367-662l514 514v34h-99ZM599-527l-66-66 249-249h99v33L599-527ZM294-666q26-26 26-64t-26-64q-26-26-64-26t-64 26q-26 26-26 64t26 64q26 26 64 26t64-26Zm202.5 203.5Q502-468 502-476t-5.5-13.5Q491-495 483-495t-13.5 5.5Q464-484 464-476t5.5 13.5Q475-457 483-457t13.5-5.5ZM294-166q26-26 26-64t-26-64q-26-26-64-26t-64 26q-26 26-26 64t26 64q26 26 64 26t64-26Z" />,
    close: <path d="m249-207-42-42 231-231-231-231 42-42 231 231 231-231 42 42-231 231 231 231-42 42-231-231-231 231Z" />,
  };
  return (
    <svg className="st-vp-glyph" width={size} height={size} viewBox="0 -960 960 960"
         fill="currentColor" aria-hidden="true" focusable="false">{shapes[name] || null}</svg>
  );
}

function Btn({ icon, label, onClick, active, disabled, size = 16, className = '' }) {
  return (
    <button type="button" title={label} aria-label={label} disabled={disabled}
            aria-pressed={active === undefined ? undefined : !!active}
            className={`st-vp-btn${active ? ' is-on' : ''} ${className}`.trim()} onClick={onClick}>
      <Glyph name={icon} size={size}/>
    </button>
  );
}

/* A popover menu anchored under its button. */
function Menu({ id, label, icon, open, onToggle, children, text }) {
  return (
    <div className="st-vp-menu">
      <button type="button" className={`st-vp-btn${open ? ' is-on' : ''}`} onClick={onToggle}
              title={label} aria-label={label} aria-haspopup="menu" aria-expanded={open}>
        {text ? <span className="st-vp-btn-text">{text}</span> : <Glyph name={icon} size={16}/>}
      </button>
      {open && <div className="st-vp-pop" role="menu" id={id}>{children}</div>}
    </div>
  );
}

export default function MediaPlayer({
  src, name, kind = 'video', mediaKey, startAt, token,
  onPrev, onNext, hasPrev, hasNext, onError, onDownload, onOpenTab, onPosition,
  qualities, qualityNote, trim, file,
}) {
  const isAudio = kind === 'audio';
  const prefs = useMemo(() => readJSON(PREFS_KEY, { volume: 1, muted: false, rate: 1 }), []);

  const wrapRef = useRef(null);
  const mediaRef = useRef(null);
  const qList = qualities && qualities.length ? qualities : null;
  const railRef = useRef(null);
  const hideTimer = useRef(null);
  const savedAt = useRef(0);
  const startedAt = useRef(false);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [duration, setDuration] = useState(NaN);
  const [current, setCurrent] = useState(0);
  const [bufferedTo, setBufferedTo] = useState(0);
  const [volume, setVolume] = useState(prefs.volume);
  const [muted, setMuted] = useState(prefs.muted);
  const [rate, setRate] = useState(prefs.rate);
  // Which copy plays. `qPref` is what was asked for ('auto' or an id);
  // `autoCap` is where Auto has stepped down to after stalling.
  const [qPref, setQPref] = useState(() => { try { return window.localStorage.getItem(QUALITY_KEY) || 'auto'; } catch { return 'auto'; } });
  const [autoCap, setAutoCap] = useState(null);
  const switchRef = useRef(null);       // {t, play} carried across a quality switch
  const stallsRef = useRef([]);
  const autoId = qList ? ((autoCap && qList.some((q) => q.id === autoCap)) ? autoCap : autoQuality(qList)) : null;
  const activeQ = qList ? ((qPref !== 'auto' && qList.some((q) => q.id === qPref)) ? qPref : autoId) : null;
  // Trimming: the panel, and a result being watched in place of the file.
  // A preview is not the file — nothing it does is remembered as the file's.
  const canTrim = !!trim && !isAudio && !!token && !!mediaKey && !!file;
  const [trimOpen, setTrimOpen] = useState(false);
  const [previewJob, setPreviewJob] = useState(null);
  const previewRef = useRef(null);
  previewRef.current = previewJob;
  const trimKeys = useRef(null);
  // Read by the hide timer, which may have been set before the panel opened.
  const trimOpenRef = useRef(false);
  trimOpenRef.current = trimOpen;
  const previewSrc = previewJob
    ? ((previewJob.direct_url && reachableNow(previewJob.direct_url.split('/d/')[0])) ? previewJob.direct_url
      : api.trimResultUrl(token, previewJob.id))
    : null;
  const mediaSrc = previewSrc || (activeQ ? qList.find((q) => q.id === activeQ).src : src);
  /* The source is about to change under a player that may be mid-video: the
     copies arriving, a choice from the menu, Auto stepping down. Wherever it
     was, and whether it was playing, goes with it — read here, before the
     new src reaches the element and resets both. */
  const lastSrcRef = useRef(mediaSrc);
  if (lastSrcRef.current !== mediaSrc) {
    const el = mediaRef.current;
    if (el && !switchRef.current && el.currentTime > 0) switchRef.current = { t: el.currentTime, play: !el.paused };
    lastSrcRef.current = mediaSrc;
  }
  const [loop, setLoop] = useState(false);
  const [ab, setAB] = useState({ a: null, b: null });
  const [fit, setFit] = useState('contain');
  const [rotate, setRotate] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [pip, setPip] = useState(false);
  const [uiVisible, setUiVisible] = useState(true);
  const [menu, setMenu] = useState(null);           // 'speed' | 'aspect' | 'captions' | 'audio'
  const [overlay, setOverlay] = useState(null);     // 'stats' | 'help' — they share a corner
  const stats = overlay === 'stats';
  const help = overlay === 'help';
  const setStats = useCallback((v) => setOverlay((o) => ((typeof v === 'function' ? v(o === 'stats') : v) ? 'stats' : null)), []);
  const setHelp = useCallback((v) => setOverlay((o) => ((typeof v === 'function' ? v(o === 'help') : v) ? 'help' : null)), []);
  const [remain, setRemain] = useState(false);      // time display counts down
  const [scrub, setScrub] = useState(null);         // {time, x} while pointing at the rail
  const [tracks, setTracks] = useState([]);         // text tracks
  const [activeTrack, setActiveTrack] = useState(-1);
  const [resume, setResume] = useState(0);          // offer to jump back
  const [quality, setQuality] = useState(null);     // dropped frames, for stats
  const [flash, setFlash] = useState(null);         // transient centre label

  const say = useCallback((msg) => {
    setFlash({ msg, at: Date.now() });
    window.setTimeout(() => setFlash((f) => (f && Date.now() - f.at >= 700 ? null : f)), 750);
  }, []);

  // Markers need someone to attribute them to, so a player with no session
  // behind it simply has none.
  const canMark = !!token && !!mediaKey;
  const marks = useMarkers({ token, fileId: mediaKey, enabled: canMark });
  const [markPanel, setMarkPanel] = useState(false);
  const [markFocus, setMarkFocus] = useState({ id: null, note: false });

  /* ─── element wiring ─────────────────────────────────────────────── */
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return undefined;
    el.volume = volume;
    el.muted = muted;
    el.playbackRate = rate;
    // Only on mount: later changes go through the setters below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { const el = mediaRef.current; if (el) el.volume = volume; }, [volume]);
  useEffect(() => { const el = mediaRef.current; if (el) el.muted = muted; }, [muted]);
  useEffect(() => { const el = mediaRef.current; if (el) el.playbackRate = rate; }, [rate]);
  useEffect(() => { const el = mediaRef.current; if (el) el.loop = loop && ab.b === null; }, [loop, ab.b]);

  useEffect(() => {
    writeJSON(PREFS_KEY, { volume, muted, rate });
  }, [volume, muted, rate]);

  // A new file: reset everything that belongs to the old one.
  useEffect(() => {
    setReady(false); setPlaying(false); setDuration(NaN); setCurrent(0);
    setBufferedTo(0); setAB({ a: null, b: null }); setRotate(0); setFit('contain');
    setOverlay(null); setMenu(null); setActiveTrack(-1); setTracks([]);
    setPreviewJob(null);
    startedAt.current = false;
    const saved = loadPositions()[mediaKey];
    setResume(Number.isFinite(startAt) && startAt > 0 ? 0 : (Number.isFinite(saved) && saved > RESUME_MIN ? saved : 0));
  // Keyed on the FILE, not the source: a quality switch is the same video.
  }, [mediaKey, startAt]);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return undefined;

    const onLoaded = () => {
      setReady(true);
      setDuration(el.duration);
      const sw = switchRef.current;
      if (sw) {
        switchRef.current = null;
        el.currentTime = Math.min(sw.t, Math.max(0, (el.duration || sw.t) - 0.5));
        if (sw.play) el.play().catch(() => {});
      }
      // A link that says where to start wins over the remembered position,
      // and only applies once — not on every metadata event.
      if (Number.isFinite(startAt) && startAt > 0 && !startedAt.current) {
        startedAt.current = true;
        el.currentTime = Math.min(startAt, Math.max(0, (el.duration || startAt) - 1));
      }
      const list = Array.from(el.textTracks || []);
      list.forEach((t) => { t.mode = 'disabled'; });
      setTracks(list.map((t, i) => ({ i, label: t.label || t.language || `Track ${i + 1}`, kind: t.kind })));
    };
    // Where we are goes two places: localStorage, which is what the Resume
    // prompt reads, and whoever is listening — the shell, which puts it in
    // the address bar. Both are told on pause, on a seek and on the way out,
    // because those are the moments the exact second matters.
    const mark = (force) => {
      if (previewRef.current) return;
      savePosition(mediaKey, el.currentTime, el.duration);
      // The key goes with it: the last report a player makes is on its way
      // out, by which time the page may already be showing another file, and
      // a position is only ever about the file it was measured in.
      onPosition?.(el.currentTime, force, mediaKey);
    };
    const onTime = () => {
      setCurrent(el.currentTime);
      if (ab.b !== null && el.currentTime >= ab.b) el.currentTime = ab.a ?? 0;
      if (previewRef.current) return;
      onPosition?.(el.currentTime, false, mediaKey);
      const now = Date.now();
      if (now - savedAt.current > 4000) {
        savedAt.current = now;
        savePosition(mediaKey, el.currentTime, el.duration);
      }
    };
    const onProgress = () => setBufferedTo(bufferedAhead(el));
    const onPlay = () => setPlaying(true);
    const onPause = () => { setPlaying(false); mark(true); };
    const onSeeked = () => { setCurrent(el.currentTime); mark(true); };
    const onLeaving = () => mark(true);
    const onEnded = () => {
      setPlaying(false);
      if (previewRef.current) return;
      savePosition(mediaKey, el.duration, el.duration); onPosition?.(el.currentTime, true, mediaKey);
    };
    const onWaiting = () => setWaiting(true);
    const onPlayingNow = () => setWaiting(false);
    const onRate = () => setRate(el.playbackRate);
    const onVol = () => { setVolume(el.volume); setMuted(el.muted); };
    // A result this browser cannot play (a .mkv, say) is not a broken file:
    // go back to the original and say so, rather than giving up on the player.
    const onErr = () => {
      if (previewRef.current) {
        switchRef.current = null;
        setPreviewJob(null);
        say('This browser cannot play the result — download it to check');
        return;
      }
      onError?.();
    };

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('durationchange', onLoaded);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('progress', onProgress);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('seeked', onSeeked);
    el.addEventListener('ended', onEnded);
    // pagehide rather than unload: it is the one that still fires on a phone.
    window.addEventListener('pagehide', onLeaving);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlayingNow);
    el.addEventListener('ratechange', onRate);
    el.addEventListener('volumechange', onVol);
    el.addEventListener('error', onErr);
    return () => {
      mark(true);
      window.removeEventListener('pagehide', onLeaving);
      el.removeEventListener('seeked', onSeeked);
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('durationchange', onLoaded);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('progress', onProgress);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlayingNow);
      el.removeEventListener('ratechange', onRate);
      el.removeEventListener('volumechange', onVol);
      el.removeEventListener('error', onErr);
    };
  }, [mediaKey, ab.a, ab.b, onError, startAt, onPosition, say]);

  // Smooth progress: timeupdate only fires a few times a second.
  useEffect(() => {
    if (!playing) return undefined;
    let raf = 0;
    const tick = () => {
      const el = mediaRef.current;
      if (el) { setCurrent(el.currentTime); setBufferedTo(bufferedAhead(el)); }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [playing]);

  // Stats are only computed while the overlay is open.
  useEffect(() => {
    if (!stats || isAudio) return undefined;
    const id = window.setInterval(() => {
      const el = mediaRef.current;
      if (el?.getVideoPlaybackQuality) setQuality(el.getVideoPlaybackQuality());
    }, 1000);
    return () => window.clearInterval(id);
  }, [stats, isAudio]);

  useEffect(() => {
    const onFs = () => setFullscreen(document.fullscreenElement === wrapRef.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  /* ─── commands ───────────────────────────────────────────────────── */
  const toggle = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {}); else el.pause();
  }, []);

  const seekTo = useCallback((t) => {
    const el = mediaRef.current;
    if (!el || !Number.isFinite(el.duration)) return;
    const next = Math.max(0, Math.min(el.duration, t));
    el.currentTime = next;
    setCurrent(next);
    setBufferedTo(bufferedAhead(el));
  }, []);
  const seekBy = useCallback((d) => {
    const el = mediaRef.current;
    if (el) seekTo(el.currentTime + d);
  }, [seekTo]);

  const step = useCallback((frames) => {
    const el = mediaRef.current;
    if (!el) return;
    el.pause();
    const fps = el.getVideoPlaybackQuality && el.getVideoPlaybackQuality().totalVideoFrames && el.currentTime > 0
      ? Math.max(1, Math.round(el.getVideoPlaybackQuality().totalVideoFrames / el.currentTime))
      : FALLBACK_FPS;
    seekTo(el.currentTime + frames / Math.min(fps, 120));
    say(frames > 0 ? 'Frame →' : '← Frame');
  }, [seekTo, say]);

  const nudgeVolume = useCallback((d) => {
    setMuted(false);
    setVolume((v) => {
      const next = Math.max(0, Math.min(1, Math.round((v + d) * 100) / 100));
      say(`Volume ${Math.round(next * 100)}%`);
      return next;
    });
  }, [say]);

  const nudgeRate = useCallback((dir) => {
    setRate((r) => {
      const i = SPEEDS.findIndex((s) => s >= r - 0.001);
      const next = SPEEDS[Math.max(0, Math.min(SPEEDS.length - 1, (i < 0 ? 3 : i) + dir))];
      say(`${next}×`);
      return next;
    });
  }, [say]);

  const chooseQuality = useCallback((id) => {
    setQPref(id);
    setAutoCap(null);
    stallsRef.current = [];
    try { window.localStorage.setItem(QUALITY_KEY, id); } catch { /* per visit then */ }
    setMenu(null);
  }, []);

  /* Auto steps DOWN when the connection cannot keep up: three stalls inside
     thirty seconds of playing, and the next lighter copy takes over from the
     same second. It never steps back up on its own — a copy that stalls once
     will stall again, and a picture that keeps changing is worse than a
     slightly softer one. Choosing a quality by hand turns this off. */
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !qList || qPref !== 'auto') return undefined;
    const onWaiting = () => {
      if (el.paused || el.seeking || el.currentTime < 1) return;
      const now = Date.now();
      stallsRef.current = [...stallsRef.current.filter((t) => now - t < 30000), now];
      if (stallsRef.current.length < 3) return;
      const lighter = qList.filter((q) => q.id !== 'original' && q.bitrate < (qList.find((x) => x.id === activeQ)?.bitrate || 0))
        .sort((a, b) => b.bitrate - a.bitrate)[0];
      if (lighter) {
        stallsRef.current = [];
        setAutoCap(lighter.id);
        setFlash({ msg: `Auto · ${lighter.label}`, at: now });
      }
    };
    el.addEventListener('waiting', onWaiting);
    return () => el.removeEventListener('waiting', onWaiting);
  }, [qList, qPref, activeQ]);

  const cycleAB = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    setAB((s) => {
      if (s.a === null) { say(`A → ${fmtTime(el.currentTime)}`); return { a: el.currentTime, b: null }; }
      if (s.b === null) {
        if (el.currentTime <= s.a + 0.2) { say('B must be after A'); return s; }
        say(`Repeating ${fmtTime(s.a)}–${fmtTime(el.currentTime)}`);
        return { a: s.a, b: el.currentTime };
      }
      say('Repeat off');
      return { a: null, b: null };
    });
  }, [say]);

  const snapshot = useCallback(() => {
    const el = mediaRef.current;
    if (!el || isAudio || !el.videoWidth) return;
    try {
      const c = document.createElement('canvas');
      c.width = el.videoWidth; c.height = el.videoHeight;
      c.getContext('2d').drawImage(el, 0, 0, c.width, c.height);
      c.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const base = (name || 'frame').replace(/\.[^.]+$/, '');
        a.href = url;
        a.download = `${base} @${fmtTime(el.currentTime).replace(/:/g, '-')}.png`;
        a.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 10000);
        say('Snapshot saved');
      }, 'image/png');
    } catch { say('Snapshot blocked'); }
  }, [isAudio, name, say]);

  const toggleFullscreen = useCallback(() => {
    const box = wrapRef.current;
    if (!box) return;
    if (document.fullscreenElement === box) document.exitFullscreen?.();
    else box.requestFullscreen?.().catch(() => {});
  }, []);

  const togglePip = useCallback(async () => {
    const el = mediaRef.current;
    if (!el || isAudio || !document.pictureInPictureEnabled) return;
    try {
      if (document.pictureInPictureElement === el) { await document.exitPictureInPicture(); setPip(false); }
      else { await el.requestPictureInPicture(); setPip(true); }
    } catch { say('Picture-in-picture unavailable'); }
  }, [isAudio, say]);

  const pickTrack = useCallback((i) => {
    const el = mediaRef.current;
    if (!el) return;
    Array.from(el.textTracks || []).forEach((t, idx) => { t.mode = idx === i ? 'showing' : 'disabled'; });
    setActiveTrack(i);
    setMenu(null);
  }, []);

  /* A marker is usually the start of a note, so adding one stops the video:
     the moment should hold still while someone writes about it. */
  const { add: addMarkerAt, reload: reloadMarkers, markers } = marks;
  const addMarker = useCallback(async () => {
    const el = mediaRef.current;
    if (!canMark || !el || !Number.isFinite(el.duration)) return;
    const at = el.currentTime;
    el.pause();
    const d = await addMarkerAt(at);
    setMarkPanel(true);
    if (d?.created) {
      setMarkFocus({ id: d.created, note: true });
      say(`Marked ${fmtTime(at)}`);
    }
  }, [canMark, addMarkerAt, say]);

  const jumpMarker = useCallback((dir) => {
    const el = mediaRef.current;
    if (!el || !markers.length) return;
    const t = el.currentTime;
    // Back goes to the one before, not the one just passed: a second after a
    // marker you are "at" it, the way a chapter restarts on a first press.
    const target = dir > 0
      ? markers.find((m) => m.at > t + 0.25)
      : [...markers].reverse().find((m) => m.at < t - 1);
    if (!target) { say(dir > 0 ? 'No later marker' : 'No earlier marker'); return; }
    seekTo(target.at);
    setMarkFocus({ id: target.id, note: false });
    say(`${fmtTime(target.at)} · ${target.mine ? 'you' : target.username}`);
  }, [markers, seekTo, say]);

  const pickMarker = useCallback((m) => {
    seekTo(m.at);
    setMarkPanel(true);
    setMarkFocus({ id: m.id, note: false });
  }, [seekTo]);
  const focusMarker = useCallback((id) => setMarkFocus((f) => (f.id === id ? f : { id, note: false })), []);
  const closeMarkers = useCallback(() => setMarkPanel(false), []);
  const markerNow = useCallback(() => mediaRef.current?.currentTime || 0, []);
  // Escape in a note box hands the keys back to the player.
  const leaveNote = useCallback(() => {
    document.activeElement?.blur?.();
    wrapRef.current?.focus();
  }, []);
  // Opening the list is a good moment to catch up with everyone else.
  useEffect(() => { if (markPanel) reloadMarkers(); }, [markPanel, reloadMarkers]);

  /* ─── trimming ──────────────────────────────────────────────────── */
  /* Watching a result swaps the source and starts it from the top; going
     back puts the original where it was. */
  const origAt = useRef(0);
  const choosePreview = useCallback((job) => {
    const el = mediaRef.current;
    if (job && !previewRef.current) origAt.current = el?.currentTime || 0;
    switchRef.current = job ? { t: 0, play: true } : { t: origAt.current, play: false };
    setPreviewJob(job);
  }, []);
  const closeTrim = useCallback(() => {
    setTrimOpen(false);
    if (previewRef.current) choosePreview(null);
    wrapRef.current?.focus();
  }, [choosePreview]);
  // Asked for from elsewhere (a trim in the transfers list): open on it.
  const focus = trim?.focus;
  useEffect(() => {
    if (!canTrim || !focus || focus.fileId !== mediaKey) return;
    setTrimOpen(true);
    const job = (trim.jobs || []).find((j) => j.id === focus.jobId);
    if (job && job.status === 'ready') choosePreview(job);
    trim.setFocus(null);
  }, [canTrim, focus, mediaKey, trim, choosePreview]);
  // A result that stops being watchable (saved, discarded) stops playing.
  useEffect(() => {
    if (!previewJob || !trim) return;
    const j = (trim.jobs || []).find((x) => x.id === previewJob.id);
    if (!j || !['ready', 'committing'].includes(j.status)) choosePreview(null);
  }, [trim, previewJob, choosePreview]);

  /* ─── keyboard ───────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      const box = wrapRef.current;
      // Only when this player is the thing being used: it shares the page
      // with a file list that has its own arrow keys.
      if (!box || !(box.contains(document.activeElement) || box.matches(':hover') || document.fullscreenElement === box)) return;
      const k = e.key;
      const hit = (fn) => { e.preventDefault(); e.stopPropagation(); fn(); };
      // An open trim panel has first say: I and O are in and out there.
      if (trimOpen && trimKeys.current && !(menu || overlay) && trimKeys.current(e)) return;
      if (k === 'e' && canTrim) return hit(() => setTrimOpen((v) => !v));

      if (k === ' ' || k === 'k') return hit(toggle);
      if (k === 'ArrowRight') return hit(() => seekBy(e.shiftKey ? 60 : 5));
      if (k === 'ArrowLeft') return hit(() => seekBy(e.shiftKey ? -60 : -5));
      if (k === 'l') return hit(() => seekBy(10));
      if (k === 'j') return hit(() => seekBy(-10));
      if (k === 'ArrowUp') return hit(() => nudgeVolume(0.05));
      if (k === 'ArrowDown') return hit(() => nudgeVolume(-0.05));
      if (k === 'm') return hit(() => setMuted((m) => !m));
      if (k === 'M' && canMark) return hit(addMarker);
      if (k === '<') return hit(() => jumpMarker(-1));
      if (k === '>') return hit(() => jumpMarker(1));
      if (k === 'f') return hit(toggleFullscreen);
      if (k === 'o') return hit(togglePip);
      if (k === 't' && onOpenTab) return hit(() => onOpenTab(mediaRef.current?.currentTime || 0));
      if (k === ',') return hit(() => step(-1));
      if (k === '.') return hit(() => step(1));
      if (k === '[') return hit(() => nudgeRate(-1));
      if (k === ']') return hit(() => nudgeRate(1));
      if (k === '=') return hit(() => { setRate(1); say('1×'); });
      if (k === 'b') return hit(cycleAB);
      if (k === 'r') return hit(() => setLoop((v) => { say(v ? 'Loop off' : 'Loop on'); return !v; }));
      if (k === 's') return hit(snapshot);
      if (k === 'c') return hit(() => { if (tracks.length) pickTrack(activeTrack >= 0 ? -1 : 0); });
      if (k === 'a') return hit(() => setFit((f) => FITS[(FITS.findIndex((x) => x.id === f) + 1) % FITS.length].id));
      if (k === 'x') return hit(() => setRotate((r) => (r + 90) % 360));
      if (k === 'i') return hit(() => setStats((v) => !v));
      if (k === '?' || (k === '/' && e.shiftKey)) return hit(() => setHelp((v) => !v));
      if (k === 'n' && hasNext) return hit(() => onNext?.());
      if (k === 'p' && hasPrev) return hit(() => onPrev?.());
      if (k === 'Home') return hit(() => seekTo(0));
      if (k === 'End') return hit(() => seekTo((mediaRef.current?.duration || 0) - 0.1));
      if (k === 'Escape' && (menu || overlay)) return hit(() => { setMenu(null); setOverlay(null); });
      if (k === 'Escape' && markPanel) return hit(() => setMarkPanel(false));
      if (k === 'Escape' && trimOpen) return hit(closeTrim);
      if (/^[0-9]$/.test(k)) {
        const el = mediaRef.current;
        if (el && Number.isFinite(el.duration)) return hit(() => seekTo((Number(k) / 10) * el.duration));
      }
      return undefined;
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [toggle, seekBy, seekTo, step, nudgeVolume, nudgeRate, cycleAB, snapshot, toggleFullscreen,
      togglePip, pickTrack, tracks.length, activeTrack, hasNext, hasPrev, onNext, onPrev, menu, overlay,
      setStats, setHelp, say, onOpenTab, canMark, addMarker, jumpMarker, markPanel, trimOpen, canTrim, closeTrim]);

  /* ─── controls auto-hide ─────────────────────────────────────────── */
  const wake = useCallback(() => {
    setUiVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (mediaRef.current && !mediaRef.current.paused && !menu && !help && !markPanel && !trimOpenRef.current) setUiVisible(false);
    }, 2600);
  }, [menu, help, markPanel]);
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);
  useEffect(() => { if (!playing || menu || help || markPanel || trimOpen) setUiVisible(true); }, [playing, menu, help, markPanel, trimOpen]);

  /* ─── seek rail ──────────────────────────────────────────────────── */
  const railTime = useCallback((clientX) => {
    const rail = railRef.current;
    const el = mediaRef.current;
    if (!rail || !el || !Number.isFinite(el.duration)) return null;
    const r = rail.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return { time: p * el.duration, x: p * r.width };
  }, []);
  const onRailDown = (e) => {
    const at = railTime(e.clientX);
    if (!at) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekTo(at.time);
    setScrub(at);
  };
  const onRailMove = (e) => {
    const at = railTime(e.clientX);
    if (!at) return;
    setScrub(at);
    if (e.buttons === 1) seekTo(at.time);
  };
  const onRailUp = (e) => {
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
  };

  const pct = Number.isFinite(duration) && duration > 0 ? (current / duration) * 100 : 0;
  const bufPct = Number.isFinite(duration) && duration > 0 ? Math.min(100, (bufferedTo / duration) * 100) : 0;
  const abStyle = ab.a !== null && Number.isFinite(duration) && duration > 0
    ? { left: `${(ab.a / duration) * 100}%`, width: `${(((ab.b ?? current) - ab.a) / duration) * 100}%` }
    : null;
  const volIcon = muted || volume === 0 ? 'mute' : volume < 0.5 ? 'volumeLow' : 'volume';
  const el = mediaRef.current;

  const surface = isAudio ? (
    <div className="st-vp-audioart">
      <Glyph name="audio" size={54}/>
      <span className="st-vp-audioname">{name}</span>
    </div>
  ) : null;

  return (
    <div
      ref={wrapRef}
      className={`st-vp${fullscreen ? ' is-fs' : ''}${uiVisible ? '' : ' is-idle'}${isAudio ? ' is-audio' : ''}${markPanel && canMark ? ' has-mk' : ''}`}
      tabIndex={-1}
      onMouseMove={wake}
      onMouseLeave={() => { if (playing && !menu && !help && !markPanel && !trimOpen) setUiVisible(false); }}
      onPointerDown={wake}
    >
      {/* Fullscreen hides the page around the player, file name and all, so the
          name comes along — and fades with the controls. */}
      {fullscreen && name && (
        <div className="st-vp-title" title={name}>
          <span className="st-vp-title-text">{name}</span>
        </div>
      )}
      <div className="st-vp-stage"
           onClick={(e) => { if (e.target === e.currentTarget || e.target.tagName === 'VIDEO') toggle(); }}
           onDoubleClick={(e) => { if (!isAudio && (e.target === e.currentTarget || e.target.tagName === 'VIDEO')) toggleFullscreen(); }}>
        {isAudio ? (
          <>
            {surface}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio ref={mediaRef} src={mediaSrc} preload="metadata" className="st-vp-audio"/>
          </>
        ) : (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            ref={mediaRef}
            src={mediaSrc}
            preload="metadata"
            playsInline
            className="st-vp-video"
            style={{ objectFit: fit, transform: rotate ? `rotate(${rotate}deg)` : undefined }}
          />
        )}

        {waiting && playing && <div className="st-vp-spinner" aria-label="Buffering"/>}

        {!playing && ready && (
          <button type="button" className="st-vp-bigplay" onClick={toggle} aria-label="Play">
            <Glyph name="play" size={30}/>
          </button>
        )}

        {flash && <div className="st-vp-flash" key={flash.at}>{flash.msg}</div>}

        {previewJob && (
          <div className="st-vp-resume st-trim-banner">
            <span>Trimmed result — not saved yet</span>
            <button type="button" className="st-vp-link" onClick={() => choosePreview(null)}>Back to the original</button>
          </div>
        )}
        {canTrim && !previewJob && <TrimNotice trim={trim} fileId={mediaKey}/>}

        {resume > 0 && current < 1 && !previewJob && (
          <div className="st-vp-resume">
            <span>Left off at {fmtTime(resume)}</span>
            <button type="button" className="st-vp-link" onClick={() => { seekTo(resume); setResume(0); }}>Resume</button>
            <button type="button" className="st-vp-link st-vp-link--dim" onClick={() => setResume(0)}>Start over</button>
          </div>
        )}

        {stats && (
          <div className="st-vp-stats">
            <button type="button" className="st-vp-statsx" onClick={() => setStats(false)} aria-label="Close stats">
              <Glyph name="close" size={12}/>
            </button>
            <dl>
              {!isAudio && <><dt>Size</dt><dd>{el?.videoWidth ? `${el.videoWidth}×${el.videoHeight}` : '—'}</dd></>}
              <dt>Position</dt><dd>{fmtTime(current)} / {fmtTime(duration)}</dd>
              <dt>Buffered</dt><dd>{fmtTime(Math.max(0, bufferedTo - current))} ahead</dd>
              <dt>Speed</dt><dd>{rate}×</dd>
              <dt>Volume</dt><dd>{muted ? 'muted' : `${Math.round(volume * 100)}%`}</dd>
              {!isAudio && (
                <>
                  <dt>Frames</dt>
                  <dd>{quality ? `${quality.droppedVideoFrames} dropped / ${quality.totalVideoFrames}` : '—'}</dd>
                </>
              )}
              <dt>Ready</dt><dd>{['nothing', 'metadata', 'current', 'future', 'enough'][el?.readyState ?? 0]}</dd>
            </dl>
          </div>
        )}

        {help && (
          <div className="st-vp-help">
            <button type="button" className="st-vp-statsx" onClick={() => setHelp(false)} aria-label="Close shortcuts">
              <Glyph name="close" size={12}/>
            </button>
            <h4>Keys</h4>
            <ul>
              <li><kbd>space</kbd> play / pause</li>
              <li><kbd>←</kbd> <kbd>→</kbd> 5s · with <kbd>shift</kbd> 60s</li>
              <li><kbd>j</kbd> <kbd>l</kbd> 10s</li>
              <li><kbd>,</kbd> <kbd>.</kbd> frame step</li>
              <li><kbd>[</kbd> <kbd>]</kbd> speed · <kbd>=</kbd> reset</li>
              <li><kbd>↑</kbd> <kbd>↓</kbd> volume · <kbd>m</kbd> mute</li>
              <li><kbd>0</kbd>–<kbd>9</kbd> jump to 0–90%</li>
              <li><kbd>b</kbd> A→B repeat · <kbd>r</kbd> loop</li>
              <li><kbd>f</kbd> fullscreen · <kbd>o</kbd> pop out</li>
              <li><kbd>s</kbd> snapshot · <kbd>c</kbd> subtitles</li>
              <li><kbd>a</kbd> fit · <kbd>x</kbd> rotate · <kbd>i</kbd> stats</li>
              <li><kbd>p</kbd> <kbd>n</kbd> previous / next file</li>
              <li><kbd>t</kbd> open in a new tab</li>
              {canTrim && <li><kbd>e</kbd> trim · then <kbd>i</kbd> <kbd>o</kbd> in / out · <kbd>shift</kbd>+<kbd>i</kbd>/<kbd>o</kbd> go to them · <kbd>shift</kbd>+<kbd>p</kbd> play the cut · <kbd>shift</kbd>+<kbd>s</kbd> split</li>}
              {canMark && <li><kbd>shift</kbd>+<kbd>m</kbd> add a marker · <kbd>&lt;</kbd> <kbd>&gt;</kbd> previous / next</li>}
            </ul>
          </div>
        )}

        {canMark && markPanel && (
          <MarkerPanel
            marks={marks}
            current={Math.floor(current * 2) / 2}
            getNow={markerNow}
            fmtTime={fmtTime}
            focusId={markFocus.id}
            focusNote={markFocus.note}
            onFocus={focusMarker}
            onSeek={pickMarker}
            onAdd={addMarker}
            onClose={closeMarkers}
            onEscape={leaveNote}
          />
        )}
      </div>

      <div className="st-vp-bar">
        {canTrim && trimOpen && (
          <TrimTools
            trim={trim}
            file={file}
            keysRef={trimKeys}
            onClose={closeTrim}
            player={{ mediaRef, duration, current, seekTo, say, previewJob, setPreviewJob: choosePreview }}
          />
        )}
        <div className="st-vp-railwrap">
          <div ref={railRef} className="st-vp-rail" role="slider" tabIndex={0}
               aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(duration) || 0}
               aria-valuenow={Math.round(current)} aria-valuetext={`${fmtTime(current)} of ${fmtTime(duration)}`}
               onPointerDown={onRailDown} onPointerMove={onRailMove} onPointerUp={onRailUp}
               onPointerLeave={() => setScrub(null)}
               onKeyDown={(e) => {
                 if (e.key === 'ArrowRight') { e.preventDefault(); seekBy(5); }
                 else if (e.key === 'ArrowLeft') { e.preventDefault(); seekBy(-5); }
               }}>
            <div className="st-vp-buf" style={{ width: `${bufPct}%` }}/>
            {abStyle && <div className="st-vp-ab" style={abStyle}/>}
            <div className="st-vp-fill" style={{ width: `${pct}%` }}/>
            <div className="st-vp-knob" style={{ left: `${pct}%` }}/>
            {scrub && (
              <div className="st-vp-scrub" style={{ left: `${scrub.x}px` }}>{fmtTime(scrub.time)}</div>
            )}
          </div>
          {canMark && !previewJob && (
            <MarkerPins markers={markers} duration={duration} fmtTime={fmtTime}
                        focusId={markPanel ? markFocus.id : null} onPick={pickMarker}/>
          )}
        </div>

        <div className="st-vp-row">
          <Btn icon="prev" label="Previous file (p)" onClick={() => onPrev?.()} disabled={!hasPrev}/>
          <Btn icon={playing ? 'pause' : 'play'} label={playing ? 'Pause (space)' : 'Play (space)'}
               onClick={toggle} size={18} className="st-vp-play"/>
          <Btn icon="next" label="Next file (n)" onClick={() => onNext?.()} disabled={!hasNext}/>
          <Btn icon="back10" label="Back 10s (j)" onClick={() => seekBy(-10)}/>
          <Btn icon="fwd10" label="Forward 10s (l)" onClick={() => seekBy(10)}/>
          {!isAudio && <Btn icon="frameBack" label="Previous frame (,)" onClick={() => step(-1)}/>}
          {!isAudio && <Btn icon="frameFwd" label="Next frame (.)" onClick={() => step(1)}/>}

          <button type="button" className="st-vp-time tnum" onClick={() => setRemain((v) => !v)}
                  title="Click to count down">
            {fmtTime(current)}
            <span className="st-vp-time-sep">/</span>
            {remain ? `-${fmtTime(Math.max(0, (duration || 0) - current))}` : fmtTime(duration)}
          </button>

          <div className="st-vp-spacer"/>

          <div className="st-vp-vol">
            <Btn icon={volIcon} label={muted ? 'Unmute (m)' : 'Mute (m)'} onClick={() => setMuted((m) => !m)}/>
            <input className="st-vp-volrail" type="range" min="0" max="1" step="0.01"
                   value={muted ? 0 : volume} aria-label="Volume"
                   onChange={(e) => { setMuted(false); setVolume(Number(e.target.value)); }}/>
          </div>

          <Menu id="st-vp-speed" label="Speed" icon="speed" text={`${rate}×`}
                open={menu === 'speed'} onToggle={() => setMenu((m) => (m === 'speed' ? null : 'speed'))}>
            {SPEEDS.map((s) => (
              <button key={s} type="button" role="menuitemradio" aria-checked={rate === s}
                      className={`st-vp-popitem${rate === s ? ' is-on' : ''}`}
                      onClick={() => { setRate(s); setMenu(null); }}>{s}×</button>
            ))}
          </Menu>

          {qList && !previewJob && (
            <Menu id="st-vp-quality" label="Quality" icon="speed"
                  text={activeQ === 'original' ? 'Orig' : `${activeQ}p`}
                  open={menu === 'quality'} onToggle={() => setMenu((m) => (m === 'quality' ? null : 'quality'))}>
              <button type="button" role="menuitemradio" aria-checked={qPref === 'auto'}
                      className={`st-vp-popitem${qPref === 'auto' ? ' is-on' : ''}`}
                      onClick={() => chooseQuality('auto')}>
                Auto <span className="st-vp-popnote">{autoId === 'original' ? 'original' : `${autoId}p`}</span>
              </button>
              {qList.map((q) => (
                <button key={q.id} type="button" role="menuitemradio" aria-checked={qPref === q.id}
                        className={`st-vp-popitem${qPref === q.id ? ' is-on' : ''}`}
                        onClick={() => chooseQuality(q.id)}>
                  {q.label} <span className="st-vp-popnote">{q.note}</span>
                </button>
              ))}
              {qualityNote && <><div className="st-vp-popsep"/><div className="st-vp-popinfo">{qualityNote}</div></>}
            </Menu>
          )}

          <Btn icon="ab" label="A→B repeat (b)" onClick={cycleAB} active={ab.a !== null}/>
          <Btn icon="loop" label="Loop (r)" onClick={() => setLoop((v) => !v)} active={loop}/>

          {canMark && <Btn icon="markerAdd" label="Add a marker here (shift+M)" onClick={addMarker} disabled={!ready}/>}
          {canMark && (
            <button type="button" className={`st-vp-btn st-mk-toggle${markPanel ? ' is-on' : ''}`}
                    onClick={() => setMarkPanel((v) => !v)} aria-pressed={markPanel}
                    title="Markers and notes" aria-label={`Markers and notes (${markers.length})`}>
              <Glyph name="marker" size={16}/>
              {markers.length > 0 && <span className="st-mk-badge tnum">{markers.length}</span>}
            </button>
          )}

          {canTrim && (
            <Btn icon="cut" label={trimOpen ? 'Close trim (e)' : 'Trim this video (e)'}
                 onClick={() => (trimOpen ? closeTrim() : setTrimOpen(true))} active={trimOpen} disabled={!ready && !trimOpen}/>
          )}

          {!isAudio && (
            <Menu id="st-vp-aspect" label="Fit and rotation" icon="aspect"
                  open={menu === 'aspect'} onToggle={() => setMenu((m) => (m === 'aspect' ? null : 'aspect'))}>
              {FITS.map((f) => (
                <button key={f.id} type="button" role="menuitemradio" aria-checked={fit === f.id}
                        className={`st-vp-popitem${fit === f.id ? ' is-on' : ''}`}
                        onClick={() => { setFit(f.id); setMenu(null); }}>{f.label}</button>
              ))}
              <div className="st-vp-popsep"/>
              <button type="button" className="st-vp-popitem" onClick={() => setRotate((r) => (r + 90) % 360)}>
                Rotate 90° <span className="st-vp-popnote">{rotate}°</span>
              </button>
            </Menu>
          )}

          {tracks.length > 0 && (
            <Menu id="st-vp-cc" label="Subtitles" icon="captions"
                  open={menu === 'captions'} onToggle={() => setMenu((m) => (m === 'captions' ? null : 'captions'))}>
              <button type="button" role="menuitemradio" aria-checked={activeTrack === -1}
                      className={`st-vp-popitem${activeTrack === -1 ? ' is-on' : ''}`}
                      onClick={() => pickTrack(-1)}>Off</button>
              {tracks.map((t) => (
                <button key={t.i} type="button" role="menuitemradio" aria-checked={activeTrack === t.i}
                        className={`st-vp-popitem${activeTrack === t.i ? ' is-on' : ''}`}
                        onClick={() => pickTrack(t.i)}>{t.label}</button>
              ))}
            </Menu>
          )}

          {!isAudio && <Btn icon="snapshot" label="Snapshot this frame (s)" onClick={snapshot}/>}
          <Btn icon="stats" label="Stats (i)" onClick={() => setStats((v) => !v)} active={stats}/>
          <Btn icon="help" label="Keyboard shortcuts (?)" onClick={() => setHelp((v) => !v)} active={help}/>
          {onOpenTab && (
            <Btn icon="newtab" label="Open in a new tab (t)"
                 onClick={() => onOpenTab(mediaRef.current?.currentTime || 0)}/>
          )}
          {!isAudio && document.pictureInPictureEnabled && (
            <Btn icon="pip" label="Pop out (o)" onClick={togglePip} active={pip}/>
          )}
          {!isAudio && (
            <Btn icon={fullscreen ? 'unfull' : 'full'} label={fullscreen ? 'Leave fullscreen (f)' : 'Fullscreen (f)'}
                 onClick={toggleFullscreen}/>
          )}
        </div>
      </div>
    </div>
  );
}
